import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Package, Plus, Search, Edit, Trash2, X, SlidersHorizontal, History,
  AlertTriangle, PackageX, IndianRupee, ArrowDownCircle, ArrowUpCircle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { UiSelect } from '../../components/UiSelect';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SkeletonTable, EmptyState, ButtonLoader } from '../../components/LoadingStates';
import { inventoryItemService } from '../../services/inventory/inventoryItem';
import { supplierService } from '../../services/inventory/supplier';
import {
  InventoryItem, CreateInventoryItemRequest, UNIT_OPTIONS, UnitOfMeasure,
  unitShort, stockStatus, StockStatus, AdjustmentType, MOVEMENT_TYPE_META,
} from '../../types/inventory';
import { formatCurrency, formatQty, formatDateTime } from './inventoryUtils';

interface Props {
  org: string;
  restaurantId: string;
}

interface ItemFormData {
  name: string;
  sku?: string;
  category?: string;
  unit: UnitOfMeasure;
  currentStock?: number;
  reorderLevel: number;
  parLevel?: number;
  costPerUnit: number;
  supplierId?: string;
  notes?: string;
}

const STATUS_BADGE: Record<StockStatus, { label: string; cls: string }> = {
  OK: { label: 'In Stock', cls: 'bg-green-100 text-green-800' },
  LOW: { label: 'Low Stock', cls: 'bg-amber-100 text-amber-800' },
  OUT: { label: 'Out of Stock', cls: 'bg-red-100 text-red-800' },
};

export const StockItemsTab: React.FC<Props> = ({ org, restaurantId }) => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [adjustItem, setAdjustItem] = useState<InventoryItem | null>(null);
  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; title: string; message: string; type: 'warning' | 'danger' | 'info'; confirmText: string; onConfirm: () => void }>({
    open: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: () => {},
  });

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<ItemFormData>();

  const { data: items, isLoading } = useQuery(
    ['inventory-items', org, restaurantId],
    () => inventoryItemService.getItems({ org, restaurantId }),
  );

  const { data: suppliers } = useQuery(
    ['inventory-suppliers', org, restaurantId],
    () => supplierService.getSuppliers(org, restaurantId),
  );

  const createMutation = useMutation(inventoryItemService.createItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['inventory-items']);
      closeForm();
      toast.success('Stock item created');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to create item'); },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateInventoryItemRequest> }) =>
      inventoryItemService.updateItem(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory-items']);
        closeForm();
        toast.success('Stock item updated');
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update item'); },
    },
  );

  const toggleMutation = useMutation(inventoryItemService.toggleActive, {
    onSuccess: () => {
      queryClient.invalidateQueries(['inventory-items']);
      toast.success('Status updated');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to update status'); },
  });

  const deleteMutation = useMutation(inventoryItemService.deleteItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['inventory-items']);
      toast.success('Stock item deleted');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to delete item'); },
  });

  const categories = useMemo(() => {
    const set = new Set<string>();
    items?.forEach(i => { if (i.category) set.add(i.category); });
    return Array.from(set).sort();
  }, [items]);

  const summary = useMemo(() => {
    const list = items ?? [];
    let lowStock = 0, outOfStock = 0, totalValue = 0;
    list.forEach(i => {
      const s = stockStatus(i.currentStock, i.reorderLevel);
      if (s === 'LOW') lowStock++;
      if (s === 'OUT') outOfStock++;
      totalValue += (i.currentStock || 0) * (i.costPerUnit || 0);
    });
    return { total: list.length, lowStock, outOfStock, totalValue };
  }, [items]);

  const filtered = useMemo(() => {
    let list = items ?? [];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(i =>
      i.name.toLowerCase().includes(q) ||
      i.sku?.toLowerCase().includes(q) ||
      i.category?.toLowerCase().includes(q),
    );
    if (categoryFilter) list = list.filter(i => i.category === categoryFilter);
    if (lowStockOnly) list = list.filter(i => stockStatus(i.currentStock, i.reorderLevel) !== 'OK');
    return list;
  }, [items, search, categoryFilter, lowStockOnly]);

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(null);
    reset();
  };

  const openCreate = () => {
    setEditing(null);
    reset({ unit: 'KG', currentStock: 0, reorderLevel: 0, costPerUnit: 0 });
    setIsFormOpen(true);
  };

  const openEdit = (item: InventoryItem) => {
    setEditing(item);
    reset({
      name: item.name,
      sku: item.sku,
      category: item.category,
      unit: item.unit,
      currentStock: item.currentStock,
      reorderLevel: item.reorderLevel,
      parLevel: item.parLevel,
      costPerUnit: item.costPerUnit,
      supplierId: item.supplierId,
      notes: item.notes,
    });
    setIsFormOpen(true);
  };

  const onSubmit = (data: ItemFormData) => {
    const payload: CreateInventoryItemRequest = {
      name: data.name.trim(),
      sku: data.sku?.trim() || undefined,
      category: data.category?.trim() || undefined,
      unit: data.unit,
      reorderLevel: Number(data.reorderLevel) || 0,
      parLevel: data.parLevel != null && String(data.parLevel) !== '' ? Number(data.parLevel) : undefined,
      costPerUnit: Number(data.costPerUnit) || 0,
      supplierId: data.supplierId || undefined,
      notes: data.notes?.trim() || undefined,
      organizationCode: org,
      restaurantId,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate({ ...payload, currentStock: Number(data.currentStock) || 0 });
    }
  };

  const askDelete = (item: InventoryItem) => setConfirm({
    open: true,
    title: 'Delete Stock Item',
    message: `Delete "${item.name}"? Its movement history will be retained but it will no longer appear in stock lists.`,
    type: 'danger',
    confirmText: 'Delete',
    onConfirm: () => { deleteMutation.mutate(item.id); setConfirm(c => ({ ...c, open: false })); },
  });

  const askToggle = (item: InventoryItem) => {
    const action = item.active ? 'deactivate' : 'activate';
    setConfirm({
      open: true,
      title: `${action[0].toUpperCase()}${action.slice(1)} Item`,
      message: `Are you sure you want to ${action} "${item.name}"?`,
      type: item.active ? 'warning' : 'info',
      confirmText: action[0].toUpperCase() + action.slice(1),
      onConfirm: () => { toggleMutation.mutate(item.id); setConfirm(c => ({ ...c, open: false })); },
    });
  };

  const cardStyle = {
    backgroundColor: currentTheme.colors.background.card,
    borderColor: currentTheme.colors.border.primary,
  };

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard icon={<Package className="h-5 w-5" />} label="Total Items" value={String(summary.total)} tone="primary" />
        <SummaryCard icon={<AlertTriangle className="h-5 w-5" />} label="Low Stock" value={String(summary.lowStock)} tone="amber" />
        <SummaryCard icon={<PackageX className="h-5 w-5" />} label="Out of Stock" value={String(summary.outOfStock)} tone="red" />
        <SummaryCard icon={<IndianRupee className="h-5 w-5" />} label="Stock Value" value={formatCurrency(summary.totalValue)} tone="green" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, SKU or category..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-[var(--color-border-primary)] bg-[var(--color-background-secondary)] focus:outline-none focus:border-[var(--color-primary-400)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
          />
        </div>
        <UiSelect
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[{ value: '', label: 'All categories' }, ...categories.map(c => ({ value: c, label: c }))]}
          className="w-full sm:w-52 shrink-0"
        />
        <button
          onClick={() => setLowStockOnly(v => !v)}
          className={clsx(
            'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
            lowStockOnly ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-gray-300 text-gray-600',
          )}
        >
          <AlertTriangle className="h-4 w-4" />
          Low stock
        </button>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 text-white rounded-lg btn-primary transition-colors"
          style={{ backgroundColor: currentTheme.colors.primary[600] }}
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Item
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <SkeletonTable rows={6} columns={6} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Package className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No stock items"
          description={search || categoryFilter || lowStockOnly ? 'Try adjusting your filters.' : 'Add your first stock item to start tracking inventory.'}
          action={!search && !categoryFilter && !lowStockOnly ? (
            <button onClick={openCreate} className="inline-flex items-center btn-primary">
              <Plus className="h-5 w-5 mr-2" /> Add Your First Item
            </button>
          ) : undefined}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={cardStyle}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr>
                <Th>Item</Th>
                <Th>Category</Th>
                <Th className="text-right">Stock</Th>
                <Th className="text-right">Reorder</Th>
                <Th className="text-right">Cost / Unit</Th>
                <Th className="text-right">Value</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="ui-tbody">
              {filtered.map(item => {
                const status = stockStatus(item.currentStock, item.reorderLevel);
                const badge = STATUS_BADGE[status];
                return (
                  <tr key={item.id} className={clsx(!item.active && 'opacity-50')}>
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: currentTheme.colors.text.primary }}>{item.name}</div>
                      <div className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                        {item.sku ? `SKU: ${item.sku}` : ''}{item.supplierName ? `${item.sku ? ' · ' : ''}${item.supplierName}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{item.category || '—'}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                      {formatQty(item.currentStock)} {unitShort(item.unit)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                      {formatQty(item.reorderLevel)} {unitShort(item.unit)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm" style={{ color: currentTheme.colors.text.secondary }}>{formatCurrency(item.costPerUnit)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                      {formatCurrency((item.currentStock || 0) * (item.costPerUnit || 0))}
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', badge.cls)}>{badge.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn title="Adjust stock" onClick={() => setAdjustItem(item)} color={currentTheme.colors.primary[600]} bg={currentTheme.colors.primary[50]}>
                          <SlidersHorizontal className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn title="Movement history" onClick={() => setHistoryItem(item)} color={currentTheme.colors.text.secondary} bg={currentTheme.colors.background.hover}>
                          <History className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn title="Edit" onClick={() => openEdit(item)} color={currentTheme.colors.primary[600]} bg={currentTheme.colors.primary[50]}>
                          <Edit className="h-4 w-4" />
                        </IconBtn>
                        <button
                          onClick={() => askToggle(item)}
                          className="px-2 py-1 rounded-lg text-xs font-medium"
                          style={{ backgroundColor: item.active ? '#fef2f2' : '#f0fdf4', color: item.active ? '#dc2626' : '#16a34a' }}
                          title={item.active ? 'Deactivate' : 'Activate'}
                        >
                          {item.active ? 'On' : 'Off'}
                        </button>
                        <IconBtn title="Delete" onClick={() => askDelete(item)} color="#dc2626" bg="#fef2f2">
                          <Trash2 className="h-4 w-4" />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      {isFormOpen && (
        <Modal title={editing ? 'Edit Stock Item' : 'Add Stock Item'} onClose={closeForm} wide>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Item Name" error={errors.name?.message} required>
                <input {...register('name', { required: 'Name is required' })} className={inputCls} placeholder="e.g., Tomato, Cooking Oil" />
              </Field>
              <Field label="SKU / Code">
                <input {...register('sku')} className={inputCls} placeholder="optional" />
              </Field>
              <Field label="Category">
                <input {...register('category')} className={inputCls} placeholder="e.g., Vegetables, Dairy" list="inv-categories" />
                <datalist id="inv-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
              </Field>
              <Field label="Unit" required>
                <Controller name="unit" control={control} rules={{ required: true }} render={({ field }) => (
                  <UiSelect value={field.value || ''} onChange={field.onChange}
                    options={UNIT_OPTIONS.map(u => ({ value: u.value, label: `${u.label} (${u.short})` }))} />
                )} />
              </Field>
              {!editing && (
                <Field label="Opening Stock">
                  <input type="number" step="any" min="0" {...register('currentStock')} className={inputCls} placeholder="0" />
                </Field>
              )}
              <Field label="Reorder Level (low-stock alert)" error={errors.reorderLevel?.message}>
                <input type="number" step="any" min="0" {...register('reorderLevel', { min: { value: 0, message: 'Must be ≥ 0' } })} className={inputCls} placeholder="0" />
              </Field>
              <Field label="Par Level (ideal stock)">
                <input type="number" step="any" min="0" {...register('parLevel')} className={inputCls} placeholder="optional" />
              </Field>
              <Field label="Cost per Unit (₹)" error={errors.costPerUnit?.message}>
                <input type="number" step="any" min="0" {...register('costPerUnit', { min: { value: 0, message: 'Must be ≥ 0' } })} className={inputCls} placeholder="0.00" />
              </Field>
              <Field label="Default Supplier">
                <Controller name="supplierId" control={control} render={({ field }) => (
                  <UiSelect value={field.value || ''} onChange={field.onChange} placeholder="None"
                    options={[{ value: '', label: 'None' }, ...(suppliers || []).filter(s => s.active).map(s => ({ value: s.id, label: s.name }))]} />
                )} />
              </Field>
            </div>
            <Field label="Notes">
              <textarea {...register('notes')} rows={2} className={inputCls} placeholder="optional" />
            </Field>
            {editing && (
              <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                To change the stock quantity, use “Adjust stock” so the change is recorded in the movement ledger.
              </p>
            )}
            <ModalActions
              onCancel={closeForm}
              loading={createMutation.isLoading || updateMutation.isLoading}
              submitLabel={editing ? 'Update' : 'Create'}
              loadingLabel={editing ? 'Updating...' : 'Creating...'}
            />
          </form>
        </Modal>
      )}

      {/* Adjust stock modal */}
      {adjustItem && (
        <AdjustStockModal
          item={adjustItem}
          onClose={() => setAdjustItem(null)}
          onDone={() => { setAdjustItem(null); queryClient.invalidateQueries(['inventory-items']); }}
        />
      )}

      {/* Movement history modal */}
      {historyItem && (
        <MovementHistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />
      )}

      <ConfirmationModal
        isOpen={confirm.open}
        onClose={() => setConfirm(c => ({ ...c, open: false }))}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        confirmText={confirm.confirmText}
        type={confirm.type}
        isLoading={deleteMutation.isLoading || toggleMutation.isLoading}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Adjust stock modal
// ---------------------------------------------------------------------------
const AdjustStockModal: React.FC<{ item: InventoryItem; onClose: () => void; onDone: () => void }> = ({ item, onClose, onDone }) => {
  const { currentTheme } = useTheme();
  const [type, setType] = useState<AdjustmentType>('ADJUSTMENT_IN');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation(
    () => inventoryItemService.adjustStock(item.id, { type, quantity: Number(quantity), reason: reason.trim() || undefined }),
    {
      onSuccess: () => { toast.success('Stock adjusted'); onDone(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to adjust stock'); },
    },
  );

  const qtyNum = Number(quantity) || 0;
  const isOut = type !== 'ADJUSTMENT_IN';
  const projected = isOut ? item.currentStock - qtyNum : item.currentStock + qtyNum;
  const invalid = qtyNum <= 0 || (isOut && projected < 0);

  const options: { value: AdjustmentType; label: string }[] = [
    { value: 'ADJUSTMENT_IN', label: 'Add Stock (+)' },
    { value: 'ADJUSTMENT_OUT', label: 'Remove Stock (−)' },
    { value: 'WASTAGE', label: 'Wastage / Spoilage' },
  ];

  return (
    <Modal title={`Adjust Stock — ${item.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.hover }}>
          <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>Current stock</span>
          <span className="font-semibold" style={{ color: currentTheme.colors.text.primary }}>
            {formatQty(item.currentStock)} {unitShort(item.unit)}
          </span>
        </div>

        <Field label="Adjustment Type">
          <div className="grid grid-cols-3 gap-2">
            {options.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => setType(o.value)}
                className="px-2 py-2 rounded-lg text-xs font-medium border transition-colors"
                style={{
                  backgroundColor: type === o.value ? currentTheme.colors.primary[50] : 'transparent',
                  borderColor: type === o.value ? currentTheme.colors.primary[400] : currentTheme.colors.border.primary,
                  color: type === o.value ? currentTheme.colors.primary[700] : currentTheme.colors.text.secondary,
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label={`Quantity (${unitShort(item.unit)})`}>
          <input type="number" step="any" min="0" value={quantity} onChange={e => setQuantity(e.target.value)} className={inputCls} placeholder="0" autoFocus />
        </Field>

        <Field label="Reason / Note">
          <input value={reason} onChange={e => setReason(e.target.value)} className={inputCls} placeholder="e.g., stock count correction, spillage" />
        </Field>

        {qtyNum > 0 && (
          <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: isOut ? '#fef2f2' : '#f0fdf4' }}>
            <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: isOut ? '#dc2626' : '#16a34a' }}>
              {isOut ? <ArrowDownCircle className="h-4 w-4" /> : <ArrowUpCircle className="h-4 w-4" />}
              New stock level
            </span>
            <span className="font-semibold" style={{ color: invalid ? '#dc2626' : currentTheme.colors.text.primary }}>
              {formatQty(projected)} {unitShort(item.unit)}
            </span>
          </div>
        )}
        {invalid && qtyNum > 0 && isOut && projected < 0 && (
          <p className="text-sm text-red-600">Cannot remove more than the available stock.</p>
        )}

        <ModalActions
          onCancel={onClose}
          loading={mutation.isLoading}
          disabled={invalid}
          submitLabel="Apply Adjustment"
          loadingLabel="Applying..."
          onSubmit={() => mutation.mutate()}
        />
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Movement history modal
// ---------------------------------------------------------------------------
const MovementHistoryModal: React.FC<{ item: InventoryItem; onClose: () => void }> = ({ item, onClose }) => {
  const { currentTheme } = useTheme();
  const { data: movements, isLoading } = useQuery(
    ['inventory-movements', item.id],
    () => inventoryItemService.getMovements(item.id),
  );

  return (
    <Modal title={`Movement History — ${item.name}`} onClose={onClose} wide>
      {isLoading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : !movements || movements.length === 0 ? (
        <EmptyState
          icon={<History className="h-12 w-12" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No movements yet"
          description="Stock changes will appear here."
        />
      ) : (
        <div className="max-h-[60vh] overflow-y-auto -mx-2">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0" style={{ backgroundColor: currentTheme.colors.background.card }}>
              <tr>
                <Th>Date</Th><Th>Type</Th><Th className="text-right">Qty</Th><Th className="text-right">Balance</Th><Th>Note</Th>
              </tr>
            </thead>
            <tbody className="ui-tbody">
              {movements.map(m => {
                const meta = MOVEMENT_TYPE_META[m.type];
                const isIn = meta.direction === 'IN';
                return (
                  <tr key={m.id}>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: currentTheme.colors.text.secondary }}>{formatDateTime(m.createdAt)}</td>
                    <td className="px-3 py-2">
                      <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium', isIn ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium" style={{ color: isIn ? '#16a34a' : '#dc2626' }}>
                      {isIn ? '+' : '−'}{formatQty(m.quantity)} {unitShort(m.unit ?? item.unit)}
                    </td>
                    <td className="px-3 py-2 text-right" style={{ color: currentTheme.colors.text.primary }}>{formatQty(m.balanceAfter)} {unitShort(m.unit ?? item.unit)}</td>
                    <td className="px-3 py-2" style={{ color: currentTheme.colors.text.secondary }}>{m.reason || m.createdByName || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Small shared presentational helpers (kept local to this tab)
// ---------------------------------------------------------------------------
export const inputCls = 'ui-input';

const TONE: Record<string, { bg: string; fg: string }> = {
  primary: { bg: '#eef2ff', fg: '#4f46e5' },
  amber: { bg: '#fef3c7', fg: '#b45309' },
  red: { bg: '#fee2e2', fg: '#dc2626' },
  green: { bg: '#dcfce7', fg: '#16a34a' },
};

const SummaryCard: React.FC<{ icon: React.ReactNode; label: string; value: string; tone: keyof typeof TONE }> = ({ icon, label, value, tone }) => {
  const { currentTheme } = useTheme();
  const t = TONE[tone];
  return (
    <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 flex items-center gap-3" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
      <div className="p-2.5 rounded-lg flex-shrink-0" style={{ backgroundColor: t.bg, color: t.fg }}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>{label}</p>
        <p className="text-lg font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>{value}</p>
      </div>
    </div>
  );
};

export const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const { currentTheme } = useTheme();
  return (
    <th className={clsx('px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide', className)} style={{ color: currentTheme.colors.text.secondary }}>
      {children}
    </th>
  );
};

const IconBtn: React.FC<{ title: string; onClick: () => void; color: string; bg: string; children: React.ReactNode }> = ({ title, onClick, color, bg, children }) => (
  <button onClick={onClick} title={title} className="p-2 rounded-lg transition-colors" style={{ color, backgroundColor: bg }}>
    {children}
  </button>
);

export const Field: React.FC<{ label: string; error?: string; required?: boolean; children: React.ReactNode }> = ({ label, error, required, children }) => {
  const { currentTheme } = useTheme();
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5" style={{ color: currentTheme.colors.text.primary }}>
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }> = ({ title, onClose, children, wide }) => {
  const { currentTheme } = useTheme();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={clsx('rounded-xl w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto', wide ? 'max-w-2xl' : 'max-w-md')} style={{ backgroundColor: currentTheme.colors.background.card }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-semibold" style={{ color: currentTheme.colors.text.primary }}>{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const ModalActions: React.FC<{
  onCancel: () => void;
  loading: boolean;
  disabled?: boolean;
  submitLabel: string;
  loadingLabel: string;
  onSubmit?: () => void;   // if provided, renders a type="button"; otherwise relies on form submit
}> = ({ onCancel, loading, disabled, submitLabel, loadingLabel, onSubmit }) => {
  const { currentTheme } = useTheme();
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
      <button
        type={onSubmit ? 'button' : 'submit'}
        onClick={onSubmit}
        disabled={loading || disabled}
        className="px-4 py-2 text-white rounded-lg btn-primary transition-colors disabled:opacity-50 flex items-center"
        style={{ backgroundColor: currentTheme.colors.primary[600] }}
      >
        {loading ? <ButtonLoader text={loadingLabel} /> : submitLabel}
      </button>
    </div>
  );
};
